import { Switch as MantineSwitch, SwitchProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';
import { usePreferencesStore } from '../../../stores';
import { defaultColorScheme } from '../../../theme/theme';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedSwitchProps extends SwitchProps {
  gradient?: boolean;
  gradientVariant?: GradientVariant;
  useColorScheme?: boolean; // Use color scheme from state management
}

const Switch = forwardRef<HTMLInputElement, ThemedSwitchProps>(
  ({ gradient = true, gradientVariant = 'primary', useColorScheme = true, styles, ...rest }, ref) => {
    const theme = useMantineTheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme) {
      const effectiveColorSchemeKey = colorSchemeKey || defaultColorScheme.key;
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === effectiveColorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    if (!gradient) {
      return (
        <MantineSwitch
          ref={ref}
          styles={{
            ...styles,
            label: {
              textAlign: 'left !important' as any,
              ...accessibilityStyles,
              ...(styles as any)?.label,
            },
          }}
          {...rest}
        />
      );
    }

    return (
      <MantineSwitch
        ref={ref}
        styles={{
          ...styles,
          track: {
            transition: reducedMotion ? 'none' : 'background-color 0.2s ease, border-color 0.2s ease',
            '&[data-checked]': {
              backgroundColor: `${color} !important`,
              borderColor: `${color} !important`,
            },
            ...(styles as any)?.track,
          },
          input: {
            '&:focus + *': {
              outline: strongFocus ? `2px solid ${color} !important` : 'none',
              outlineOffset: strongFocus ? '2px' : '0',
            },
            '&:focus-within + *': {
              outline: strongFocus ? `2px solid ${color} !important` : 'none',
            },
            ...(styles as any)?.input,
          },
          label: {
            textAlign: 'left !important' as any,
            gap: '8px', // Add gap between switch and label
            ...accessibilityStyles,
            ...(styles as any)?.label,
          },
        }}
        {...rest}
      />
    );
  }
);

Switch.displayName = 'Switch';

export default Switch;
